import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Building2, Search, ChevronRight, MapPin, Calendar, Users,
    ArrowUpDown, ChevronLeft, ChevronRight as ChevronRightIcon, Plus, X
} from 'lucide-react';
import { clubsService } from '../../../services';
import { useUser } from '../../../UserContext';
import './ManageClubs.css';

const ManageClubs = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [clubs, setClubs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('name_asc');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isCreatingVenue, setIsCreatingVenue] = useState(false);
    const [createForm, setCreateForm] = useState({
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        description: '',
        introduction: '',
        website: '',
        phoneNumber: '',
        email: ''
    });
    const debounceTimeout = useRef(null);
    const isShootConnectAdmin = Boolean(user?.isAdmin);

    const fetchClubs = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: pageSize.toString(),
                sort: sortOrder,
                search: searchTerm
            });

            const data = await clubsService.getManageClubs(params);
            setClubs(data.clubs);
            setTotalCount(data.totalCount);
        } catch (err) {
            console.error(err);
            setError('Failed to load venues. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, sortOrder, searchTerm]);

    useEffect(() => {
        fetchClubs();
    }, [fetchClubs]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setPage(1);
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            fetchClubs();
        }, 300);
    };

    const handleSortChange = (e) => {
        setSortOrder(e.target.value);
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= Math.ceil(totalCount / pageSize)) {
            setPage(newPage);
        }
    };

    const resetCreateForm = () => {
        setCreateForm({
            name: '',
            address: '',
            latitude: '',
            longitude: '',
            description: '',
            introduction: '',
            website: '',
            phoneNumber: '',
            email: ''
        });
    };

    const handleCreateVenue = async () => {
        if (!createForm.name.trim() || !createForm.address.trim()) {
            setError('Venue name and address are required.');
            return;
        }

        setIsCreatingVenue(true);
        setError(null);

        try {
            const payload = {
                name: createForm.name.trim(),
                address: createForm.address.trim(),
                latitude: Number(createForm.latitude || 0),
                longitude: Number(createForm.longitude || 0),
                description: createForm.description.trim() || null,
                introduction: createForm.introduction.trim() || null,
                website: createForm.website.trim() || null,
                phoneNumber: createForm.phoneNumber.trim() || null,
                email: createForm.email.trim() || null,
                ownerId: user?.userId || user?.id || 0
            };

            const createdClub = await clubsService.createClub(payload);
            setShowCreateModal(false);
            resetCreateForm();
            await fetchClubs();
            navigate(`/dashboard/club/${createdClub.clubId}/dashboard`);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to create venue. Please try again later.');
        } finally {
            setIsCreatingVenue(false);
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="manage-clubs-page">
            {/* Header */}
            <div className="manage-clubs-header">
                <div className="manage-clubs-header-content">
                    <h1>Manage Venues</h1>
                    <p>View and manage your clubs, ranges, and estates</p>
                </div>
                <div className="manage-clubs-header-stats">
                    <div className="manage-clubs-stat">
                        <Building2 size={20} />
                        <span className="manage-clubs-stat-value">{totalCount}</span>
                        <span className="manage-clubs-stat-label">Total Venues</span>
                    </div>
                    {isShootConnectAdmin && (
                        <button
                            type="button"
                            className="manage-section-add-btn"
                            onClick={() => {
                                setError(null);
                                setShowCreateModal(true);
                            }}
                        >
                            <Plus size={18} />
                            Add Venue
                        </button>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="manage-clubs-error">
                    <span>{error}</span>
                </div>
            )}

            {/* Search & Filter Bar */}
            <div className="manage-clubs-toolbar">
                <div className="manage-clubs-search">
                    <Search size={18} className="manage-clubs-search-icon" />
                    <input
                        type="text"
                        placeholder="Search venues by name or address..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
                <div className="manage-clubs-sort">
                    <ArrowUpDown size={16} />
                    <select value={sortOrder} onChange={handleSortChange}>
                        <option value="name_asc">Name (A-Z)</option>
                        <option value="name_desc">Name (Z-A)</option>
                        <option value="created_asc">Created (Oldest)</option>
                        <option value="created_desc">Created (Newest)</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading venues...</p>
                </div>
            ) : clubs.length === 0 ? (
                <div className="manage-clubs-empty">
                    <div className="manage-clubs-empty-icon">
                        <Building2 size={48} />
                    </div>
                    <h3>No Venues Found</h3>
                    <p>
                        {searchTerm 
                            ? `No venues match "${searchTerm}"`
                            : "You don't have any venues to manage yet."}
                    </p>
                    {searchTerm && (
                        <button onClick={() => setSearchTerm('')} className="manage-clubs-clear-btn">
                            Clear search
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* Clubs Grid */}
                    <div className="manage-clubs-grid">
                        {clubs.map((club) => (
                            <div key={club.clubId} className="manage-club-card">
                                <div className="manage-club-card-header">
                                    <div className="manage-club-icon">
                                        <Building2 size={24} />
                                    </div>
                                    <div className="manage-club-info">
                                        <h3>{club.name}</h3>
                                        {club.address && (
                                            <p className="manage-club-address">
                                                <MapPin size={14} />
                                                {club.address}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="manage-club-stats">
                                    {club.memberCount !== undefined && (
                                        <div className="manage-club-stat">
                                            <Users size={14} />
                                            <span>{club.memberCount} Members</span>
                                        </div>
                                    )}
                                    {club.eventCount !== undefined && (
                                        <div className="manage-club-stat">
                                            <Calendar size={14} />
                                            <span>{club.eventCount} Events</span>
                                        </div>
                                    )}
                                </div>

                                <button
                                    className="manage-club-btn"
                                    onClick={() => navigate(`/dashboard/club/${club.clubId}/dashboard`)}
                                >
                                    Manage Venue
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="manage-clubs-pagination">
                            <button
                                className="manage-clubs-page-btn"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page <= 1}
                            >
                                <ChevronLeft size={18} />
                                Previous
                            </button>
                            <div className="manage-clubs-page-info">
                                <span>Page {page} of {totalPages}</span>
                            </div>
                            <button
                                className="manage-clubs-page-btn"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= totalPages}
                            >
                                Next
                                <ChevronRightIcon size={18} />
                            </button>
                        </div>
                    )}
                </>
            )}

            {showCreateModal && isShootConnectAdmin && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add Venue</h2>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Venue Name</label>
                                <input
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                                    placeholder="Cambridge Gun Club"
                                />
                            </div>
                            <div className="form-group">
                                <label>Address</label>
                                <input
                                    value={createForm.address}
                                    onChange={(e) => setCreateForm((prev) => ({ ...prev, address: e.target.value }))}
                                    placeholder="Full venue address"
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Latitude</label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={createForm.latitude}
                                        onChange={(e) => setCreateForm((prev) => ({ ...prev, latitude: e.target.value }))}
                                        placeholder="Optional"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Longitude</label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={createForm.longitude}
                                        onChange={(e) => setCreateForm((prev) => ({ ...prev, longitude: e.target.value }))}
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>
                            <p className="form-helper-text">If coordinates are left blank, Shoot Connect will try to geocode the venue from the address.</p>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    rows={3}
                                    value={createForm.description}
                                    onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
                                    placeholder="Short internal description"
                                />
                            </div>
                            <div className="form-group">
                                <label>Introduction</label>
                                <textarea
                                    rows={3}
                                    value={createForm.introduction}
                                    onChange={(e) => setCreateForm((prev) => ({ ...prev, introduction: e.target.value }))}
                                    placeholder="Optional profile intro"
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Website</label>
                                    <input
                                        value={createForm.website}
                                        onChange={(e) => setCreateForm((prev) => ({ ...prev, website: e.target.value }))}
                                        placeholder="https://"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone Number</label>
                                    <input
                                        value={createForm.phoneNumber}
                                        onChange={(e) => setCreateForm((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Venue Email</label>
                                <input
                                    type="email"
                                    value={createForm.email}
                                    onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetCreateForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleCreateVenue}
                                disabled={isCreatingVenue}
                            >
                                {isCreatingVenue ? 'Creating...' : 'Create Venue'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageClubs;
